import { useState, useEffect, useRef, useCallback } from 'react';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export interface SearchResultToken {
  type: "token";
  mint: string;
  name: string;
  symbol: string;
  imageUrl?: string;
  creator: string;
  poolAddress?: string;
}

export interface SearchResultCreator {
  type: "creator";
  address: string;
  tokenCount: number;
  latestLaunch?: string;
  avatarUrl?: string;
  avatarVersion?: number;
  username?: string;
}

export interface SearchResultList {
  type: "list";
  id: string;
  name: string;
  slug: string;
  tokenCount: number;
  isDaily: boolean;
}

export interface SearchResults {
  tokens: SearchResultToken[];
  creators: SearchResultCreator[];
  lists: SearchResultList[];
}

export interface UseSearchReturn {
  query: string;
  setQuery: (query: string) => void;
  results: SearchResults;
  isLoading: boolean;
  error: string | null;
  hasResults: boolean;
}

const DEBOUNCE_MS = 200;

const emptyResults: SearchResults = { tokens: [], creators: [], lists: [] };

export function useSearch(): UseSearchReturn {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>(emptyResults);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const latestQueryRef = useRef<string>('');

  const performSearch = useCallback(async (searchQuery: string) => {
    // Store the query we're searching for
    latestQueryRef.current = searchQuery;

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear results for empty/short queries
    if (!searchQuery || searchQuery.trim().length < 2) {
      setResults(emptyResults);
      setIsLoading(false);
      setError(null);
      return;
    }

    const trimmedQuery = searchQuery.trim();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    setError(null);

    try {
      const searchUrl = `${SUPABASE_URL}/functions/v1/search?q=${encodeURIComponent(trimmedQuery)}`;
      
      const response = await fetch(searchUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortController.signal,
      });

      // Only apply results if this is still the latest query
      if (latestQueryRef.current !== searchQuery) {
        return;
      }

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      setResults({
        tokens: data.tokens || [],
        creators: data.creators || [],
        lists: data.lists || [],
      });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Ignore aborted requests
      }
      
      // Only set error if this is still the latest query
      if (latestQueryRef.current === searchQuery) {
        console.error('[useSearch] Error:', err);
        setError(err instanceof Error ? err.message : 'Search failed');
        setResults(emptyResults);
      }
    } finally {
      // Only update loading state if this is still the latest query
      if (latestQueryRef.current === searchQuery) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Clear any existing debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the search
    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const hasResults = results.tokens.length > 0 || results.creators.length > 0 || results.lists.length > 0;

  return {
    query,
    setQuery,
    results,
    isLoading,
    error,
    hasResults,
  };
}
